import api from '../api';
import {
  criteriaFragment,
  groupCriteriaFragment,
  reviewFormFragment,
  reviewPointFragment,
  reviewSessionFragment,
  userFragment,
} from '../fragments';
import utils from '../utils';

// initial state
const state = {
  listReviewSession: [],
  listReviewForm: [],
  reviewForm: {
    target: {},
  },
  listReviewPoint: [],
  listReviewResult: [],
};

// getters
const getters = {
  listReviewSession: state => state.listReviewSession,
  listReviewForm: state => state.listReviewForm,
  reviewForm: state => state.reviewForm,
  listReviewPoint: state => state.listReviewPoint,
  reviewable: (state, _, _2, rootGetters) =>
    state.reviewForm.status !== 'FINISH' && rootGetters['app/userNodeId'] === state.reviewForm.creator.id,
  listReviewResult: state => state.listReviewResult,
};

// actions
const actions = {
  fetchListReviewResult({ commit }, { reviewSessionId, groupCriteriaId, userId }) {
    return api
      .gql({
        query: `query viewSingleReviewResult($rwSessionId: ID!, $groupCriteriaId: ID!, $userId: ID){
          viewSingleReviewResult( rwSessionId: $rwSessionId, groupCriteriaId: $groupCriteriaId, userId: $userId) {
            rwResults {
              criteria {
                id
                title
              }
              avgPoints
              comments
              reviewerCount
            }
          }
        }`,
        enableCache: true,
        variables: {
          rwSessionId: utils.fromGlobalId(reviewSessionId).id,
          groupCriteriaId: utils.fromGlobalId(groupCriteriaId).id,
          userId: utils.fromGlobalId(userId).id,
        },
      })
      .then(data => {
        const {
          viewSingleReviewResult: { rwResults },
        } = data;
        commit('setListReviewResult', rwResults);
      });
  },
  fetchListReviewSession({ commit }, { status } = {}) {
    return api
      .gql({
        query: `query listReviewSession($status: ReviewSessionStatus) {
          listReviewSession(status: $status) {
            edges {
              node {
                ...reviewSessionFragment
              }
            }
          }
        }`,
        fragments: [userFragment, reviewSessionFragment],
        variables: {
          status,
        },
      })
      .then(data => {
        const {
          listReviewSession: { edges },
        } = data;
        const listReviewSession = edges.map(({ node }) => node);
        commit('listReviewSession', listReviewSession);
      });
  },
  fetchListReviewForm({ commit }) {
    return api
      .gql({
        query: `{
          listReviewForm {
            edges {
              node {
                ...reviewFormFragment
                rwSession {
                  ...reviewSessionFragment
                }
                reviews {
                  ...reviewPointFragment
                }
                groupCriteria {
                  ...groupCriteriaFragment
                  criteria {
                    ...criteriaFragment
                  }
                }
              }
            }
          }
        }`,
        fragments: [
          userFragment,
          reviewFormFragment,
          reviewSessionFragment,
          reviewPointFragment,
          groupCriteriaFragment,
          criteriaFragment,
        ],
      })
      .then(data => {
        const {
          listReviewForm: { edges },
        } = data;
        const listReviewForm = edges.map(({ node }) => node);
        commit('listReviewForm', listReviewForm);
      });
  },
  fetchReviewForm({ commit }, id) {
    return api
      .gql({
        query: `query node($id: ID!){
          node(id: $id) {
            ... on ReviewForm {
              ...reviewFormFragment
              reviews {
                ...reviewPointFragment
              }
              groupCriteria {
                ...groupCriteriaFragment
                criteria {
                  ...criteriaFragment
                }
              }
            }
          }
        }`,
        fragments: [userFragment, reviewFormFragment, reviewPointFragment, groupCriteriaFragment, criteriaFragment],
        variables: { id },
      })
      .then(data => {
        const { node } = data;
        commit('reviewForm', node);
      });
  },
  setReviewSessionStatus({ commit }, input) {
    return api
      .gql({
        query: `mutation setReviewSessionStatus($input: setReviewSessionStatusInput!) {
        setReviewSessionStatus(input: $input) {
          out {
            id
            status
          }
        }
      }`,
        variables: {
          input: {
            reviewSessionId: utils.fromGlobalId(input.reviewSessionId).id,
            status: input.status,
          },
        },
      })
      .then(() => {
        commit('setReviewSessionStatus', input);
      });
  },
  setReviewFormStatus({ commit }, input) {
    return api
      .gql({
        query: `mutation setReviewFormStatus($input: setReviewFormStatusInput!) {
        setReviewFormStatus(input: $input) {
          out {
            id
            status
          }
        }
      }`,
        variables: {
          input: {
            ...input,
            reviewFormId: utils.fromGlobalId(input.reviewFormId).id,
          },
        },
      })
      .then(() => {
        commit('setReviewFormStatus', input);
      });
  },
  createReviewPoint({ commit, getters, dispatch }, formData) {
    return api
      .gql({
        query: `mutation createReviewPoint($input: createReviewPointInput!) {
        createReviewPoint(input: $input) {
          out {
            id
          }
        }
      }`,
        variables: {
          input: {
            reviewFormId: utils.fromGlobalId(formData.reviewFormId).id,
            criteriaId: utils.fromGlobalId(formData.criteriaId).id,
            point: formData.point,
            comment: formData.comment,
          },
        },
      })
      .then(() => {
        const reviewForm = getters.reviewForm;
        if (reviewForm.status === utils.REVIEW_STATUS.DRAFT) {
          return dispatch('setReviewFormStatus', {
            reviewFormId: reviewForm.id,
            status: utils.REVIEW_STATUS.DOING,
          });
        }
      })
      .then(() => {
        commit(
          'app/toastr',
          {
            type: 's',
            msg: 'Saved the review',
          },
          { root: true }
        );
      });
  },
  createReviewSession({ commit }, formData) {
    return api
      .gql({
        query: `mutation createReviewSession($input: createReviewSessionInput!) {
        createReviewSession(input: $input) {
          out {
            ...reviewSessionFragment
          }
        }
        
      }`,
        variables: {
          input: {
            period: formData.period,
          },
        },
        fragments: [userFragment, reviewSessionFragment],
      })
      .then(({ createReviewSession: { out } }) => {
        commit(
          'app/toastr',
          {
            type: 's',
            title: 'Created Review Session',
            msg: JSON.stringify(out, null, 2),
          },
          { root: true }
        );
        commit('createReviewSession', out);
      });
  },
  deleteReviewSession({ commit }, reviewSessionId) {
    return api
      .gql({
        query: `mutation deleteReviewSession($input: deleteReviewSessionInput!) {
          deleteReviewSession(input: $input) {
            out
          }
        }
        `,
        variables: {
          input: {
            reviewSessionId: utils.fromGlobalId(reviewSessionId).id,
          },
        },
      })
      .then(({ deleteReviewSession: { out } }) => {
        if (out) {
          commit('deleteReviewSession', reviewSessionId);
        }
      });
  },
  deleteReviewForm({ commit }, reviewFormId) {
    return api
      .gql({
        query: `mutation deleteReviewForm($input: deleteReviewFormInput!) {
          deleteReviewForm(input: $input) {
            out
          }
        }
        `,
        variables: {
          input: {
            id: utils.fromGlobalId(reviewFormId).id,
          },
        },
      })
      .then(({ deleteReviewForm: { out } }) => {
        if (out) {
          commit('deleteReviewForm', reviewFormId);
        }
      });
  },
};

// mutations
const mutations = {
  listReviewSession(state, payload) {
    state.listReviewSession = payload;
  },
  listReviewForm(state, payload) {
    state.listReviewForm = payload;
  },
  reviewForm(state, payload) {
    state.reviewForm = payload;

    const reviewForm = payload;

    let rs = reviewForm.groupCriteria.criteria;
    const oldReviews = reviewForm.reviews;
    const oldReviewMap = oldReviews.reduce((a, c) => {
      const id = utils.composeIds(c.reviewFormId, c.criteriaId);
      return a.set(id, c);
    }, new Map());

    rs = rs.map(({ id: criteriaId, title, explanation }) => {
      const reviewFormId = utils.fromGlobalId(reviewForm.id).id;
      const id = utils.composeIds(reviewFormId, utils.fromGlobalId(criteriaId).id);
      const oldReview = oldReviewMap.get(id) || {};
      return {
        title,
        explanation,
        reviewPoint: {
          reviewFormId: reviewForm.id,
          criteriaId,
          point: oldReview.point,
          comment: oldReview.comment,
        },
      };
    });
    state.listReviewPoint = rs;
  },
  setReviewFormStatus(state, payload) {
    state.reviewForm.status = payload.status;
  },
  setListReviewResult(state, payload) {
    state.listReviewResult = payload;
  },
  setReviewSessionStatus(state, { reviewSessionId, status }) {
    const session = state.listReviewSession.find(({ id }) => id == reviewSessionId);
    if (session) {
      session.status = status;
    }
  },
  createReviewSession(state, session) {
    state.listReviewSession = [session, ...state.listReviewSession];
  },
  deleteReviewSession(state, reviewSessionId) {
    state.listReviewSession = state.listReviewSession.filter(({ id }) => id != reviewSessionId);
  },
  deleteReviewForm(state, reviewFormId) {
    state.listReviewForm = state.listReviewForm.filter(({ id }) => id != reviewFormId);
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
